<template>
  <IQOS :title="$t('online_payment.api_keys.title')" required>
    <BaseInput
      class="mt-16 w-100"
      :placeholder="$t('online_payment.api_keys.public_ph')"
      v-model="publicKey"
      fullWidth
      :disabled="!editing"
      :dark="!editing"
    />
    <BaseInput
      class="mt-16 w-100"
      fullWidth
      :dark="!editing"
      :type="editing ? 'text' : 'password'"
      :disabled="!editing"
      v-model="privateKey"
      :placeholder="$t('online_payment.api_keys.private_ph')"
    />
    <BaseInput
      class="mt-16 w-100"
      fullWidth
      :type="editing ? 'text' : 'password'"
      :disabled="!editing"
      v-model="edit_fields.stripe_webhook_key"
      placeholder="Stripe Webhook Key"
      v-if="merchant.gateway == 'stripe' && merchant.stripe_private_key"
    />
    <div v-if="isUsingTestKeys">
      <p class="iqos-comment qrw-paragraph-md orange-4-color mt-16">
        {{ $t("online_payment.api_keys.test_title") }}
      </p>
      <p class="iqos-comment qrw-paragraph-md mt-16">
        {{ $t("online_payment.api_keys.test_desc") }}
      </p>
    </div>
    <BaseButton
      variant="secondary"
      color="v1-grey-10"
      size="md"
      class="iqos-button-right iqos-group-item"
      v-if="
        merchant.gateway == 'stripe' &&
        !merchant.stripe_webhook_key &&
        merchant.stripe_private_key
      "
      @click="createStripeWebhook"
    >
      + Stripe Webhook
    </BaseButton>
    <template #actions>
      <IQOSActions
        :editing="editing"
        @edit="showEditUI"
        @cancel="hideEditUI"
        @accept="onEditFinish"
      />
    </template>
  </IQOS>
</template>

<script>
import IQOS from "@/components/IQOS.vue";
import IQOSActions from "@/components/IQOSActions.vue";
import BlackInput from "@/components/BlackInput.vue";
import DashboardButton from "@/components/DashboardButton.vue";

import StripeAPI from "@/api/stripe";

export default {
  components: {
    IQOS,
    IQOSActions,
    DashboardButton,
    BlackInput,
  },
  data() {
    return {
      editing: false,
      edit_fields: {
        liqpay_public_key: null,
        liqpay_private_key: null,

        wayforpay_merchant: null,
        wayforpay_password: null,

        payu_client_id: null,
        payu_client_secret: null,

        yookassa_shop_id: null,
        yookassa_private_key: null,

        stripe_public_key: null,
        stripe_private_key: null,
        stripe_webhook_key: null,

        touchcard_public_key: null,
        touchcard_private_key: null,

        fondy_public_key: null,
        fondy_private_key: null,

        interkassa_public_key: null,
        interkassa_private_key: null,

        tinkoff_terminal_key: null,
        tinkoff_password: null
      },
    };
  },
  props: {
    merchant: Object,
  },
  methods: {
    showEditUI() {
      this.editing = true;
    },
    hideEditUI() {
      this.editing = false;
      this.reset();
    },
    onEditFinish() {
      this.editing = false;

      this.$emit("update", this.merchant._id, this.edit_fields);
    },
    createStripeWebhook() {
      StripeAPI.createWebhook(this.merchant._id)
        .then((data) => {
          this.edit_fields.stripe_webhook_key = data.wh_secret;
        })
        .catch((error) => {
          alert(`Fail. Check your API keys.`);
        });
    },
    reset() {
      Object.keys(this.edit_fields).forEach(
        (k) => (this.edit_fields[k] = this.merchant[k])
      );
    },
  },
  computed: {
    isUsingTestKeys() {
      if (!this.privateKey) return false;
      return (
        this.privateKey.includes("test") || this.privateKey.includes("sandbox")
      );
    },
    publicKey: {
      get() {
        if (this.merchant.gateway == "liqpay") {
          return this.edit_fields.liqpay_public_key;
        }
        if (this.merchant.gateway == "wayforpay") {
          return this.edit_fields.wayforpay_merchant;
        }
        if (this.merchant.gateway == "payu") {
          return this.edit_fields.payu_client_id;
        }
        if (this.merchant.gateway == "yookassa") {
          return this.edit_fields.yookassa_shop_id;
        }

        if (this.merchant.gateway == "stripe") {
          return this.edit_fields.stripe_public_key;
        }

        if (this.merchant.gateway == "touchcard") {
          return this.edit_fields.touchcard_public_key;
        }

        if (this.merchant.gateway == "fondy") {
          return this.edit_fields.fondy_public_key;
        }

        if (this.merchant.gateway == "interkassa") {
          return this.edit_fields.interkassa_public_key;
        }

        if (this.merchant.gateway == "tinkoff") {
          return this.edit_fields.tinkoff_terminal_key;
        }

        return null;
      },
      set(v) {
        if (this.merchant.gateway == "liqpay") {
          this.edit_fields.liqpay_public_key = v;
        }
        if (this.merchant.gateway == "wayforpay") {
          this.edit_fields.wayforpay_merchant = v;
        }
        if (this.merchant.gateway == "payu") {
          this.edit_fields.payu_client_id = v;
        }
        if (this.merchant.gateway == "yookassa") {
          this.edit_fields.yookassa_shop_id = v;
        }
        if (this.merchant.gateway == "stripe") {
          this.edit_fields.stripe_public_key = v;
        }
        if (this.merchant.gateway == "touchcard") {
          this.edit_fields.touchcard_public_key = v;
        }
        if (this.merchant.gateway == "fondy") {
          this.edit_fields.fondy_public_key = v;
        }
        if (this.merchant.gateway == "interkassa") {
          this.edit_fields.interkassa_public_key = v;
        }
        if (this.merchant.gateway == "tinkoff") {
          this.edit_fields.tinkoff_terminal_key = v;
        }
      },
    },
    privateKey: {
      get() {
        if (this.merchant.gateway == "liqpay") {
          return this.edit_fields.liqpay_private_key;
        }
        if (this.merchant.gateway == "wayforpay") {
          return this.edit_fields.wayforpay_password;
        }
        if (this.merchant.gateway == "payu") {
          return this.edit_fields.payu_client_secret;
        }
        if (this.merchant.gateway == "yookassa") {
          return this.edit_fields.yookassa_private_key;
        }
        if (this.merchant.gateway == "stripe") {
          return this.edit_fields.stripe_private_key;
        }
        if (this.merchant.gateway == "touchcard") {
          return this.edit_fields.touchcard_private_key;
        }
        if (this.merchant.gateway == "fondy") {
          return this.edit_fields.fondy_private_key;
        }
        if (this.merchant.gateway == "interkassa") {
          return this.edit_fields.interkassa_private_key;
        }
        if (this.merchant.gateway == "tinkoff") {
          return this.edit_fields.tinkoff_password;
        }
        return null;
      },
      set(v) {
        if (this.merchant.gateway == "liqpay") {
          this.edit_fields.liqpay_private_key = v;
        }
        if (this.merchant.gateway == "wayforpay") {
          this.edit_fields.wayforpay_password = v;
        }
        if (this.merchant.gateway == "payu") {
          this.edit_fields.payu_client_secret = v;
        }
        if (this.merchant.gateway == "yookassa") {
          this.edit_fields.yookassa_private_key = v;
        }
        if (this.merchant.gateway == "stripe") {
          this.edit_fields.stripe_private_key = v;
        }
        if (this.merchant.gateway == "touchcard") {
          this.edit_fields.touchcard_private_key = v;
        }
        if (this.merchant.gateway == "fondy") {
          this.edit_fields.fondy_private_key = v;
        }
        if (this.merchant.gateway == "interkassa") {
          this.edit_fields.interkassa_private_key = v;
        }
        if (this.merchant.gateway == "tinkoff") {
          this.edit_fields.tinkoff_password = v;
        }
      },
    },
  },
  mounted() {
    this.reset();
  },
};
</script>

<style>
.warning {
  color: #ff6f00 !important;
}
</style>